import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { ModalControlComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-node-tree-permission',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './permission-dialog.component.html',
    styleUrls: ['./permission-dialog.component.scss'],
    standalone: false
})
export class PermissionDialogComponent {
  isSubmitted = signal(false);
  typedUsers: { [id: string]: FlatModels.UserEntityModels.User };
  userArray: { value: string; name: string; }[];
  node: FlatModels.NodeEntityModels.NodeEntityType;
  filteredUserArray: { value: string; name: string; }[];
  shareForm = new FormGroup(
    {
      id: new FormControl('')
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.UserEntityModels.UserSchema.pick({
          id: true
        })
      ),
      updateOn: 'change',
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public userSharedFacade: FlatFacades.UserSharedFacade,
              public dialogService: DialogService,
              public translate: TranslateService,
              public dynamicDialogConfig: DynamicDialogConfig) {
    this.node = structuredClone(this.dynamicDialogConfig.data.node);
    
    // Typed Users
    this.userSharedFacade.typedUsers$.pipe(takeUntilDestroyed()).subscribe((typedUsers) => {
      this.typedUsers = typedUsers;
    });
    
    // User Array
    this.userSharedFacade.usersArray$.pipe(takeUntilDestroyed()).subscribe((userArray) => {
      this.userArray = userArray;
      this.updateFilteredUserArray();
    });
    
    // Get Users
    this.userSharedFacade.requestGetUsers().subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }

  onSubmit() {
    if (this.isSubmitted() || this.shareForm.invalid || !this.shareForm.value.id) {
      return;
    }
    this.isSubmitted.set(true);

    // Update Node
    const node = this.node;
    node.shares.push(this.shareForm.value.id);
    this.nodeSharedFacade.requestUpdateNode(node, 'history.user_share_add').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.shareForm);
        this.isSubmitted.set(false);
      },
      next: () => {
        this.isSubmitted.set(false);
        this.node = node;
        this.updateFilteredUserArray(true);
      }
    });
  }

  deleteShare(id: string) {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.user_shares_remove'),
      data: {
        label: 'global.user_shares_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        const node = this.node;
        node.shares.splice(node.shares.findIndex(s => s === id), 1);
        this.nodeSharedFacade.requestUpdateNode(node, 'history.user_share_remove').subscribe({
          error: (result) => {
            ZodSharedValidator.handleError(result, this.alertControlFacade, this.shareForm);
          },
          next: () => {
            this.node = node;
            this.updateFilteredUserArray(true);
          }
        });
      }
    });
  }

  rowTrackBy(index: number,
                    item: FlatModels.UserEntityModels.User) {
    return item.id || index;
  }

  getUsers(shares: string[],
                  typedUsers: { [id: string]: FlatModels.UserEntityModels.User }) {
    return shares.map(s => typedUsers[s]).filter(s => s !== undefined) || [];
  }

  updateFilteredUserArray(force = false) {
    this.filteredUserArray = this.userArray.filter(ua => !this.node.shares.includes(ua.value) ).sort((a, b) => a.name.localeCompare(b.name));

    // Set Default Value
    if (this.filteredUserArray.length > 0) {
      const control = this.shareForm.controls['id'];
      if (control.value === '' || force) {
        control.setValue(this.filteredUserArray[0].value);
      }
    }
  }
}
